<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <template #icon>
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M4.1665 9.99984H15.8332M9.99984 4.1665V15.8332"
                            stroke="#7F56D9"
                            stroke-width="1.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </template>
                <DialogTitle>
                    {{ $t("workflows.new_workflow_modal.title") }}</DialogTitle
                >
                <DialogDescription>
                    {{
                        $t("workflows.new_workflow_modal.description")
                    }}</DialogDescription
                >
            </DialogHeader>
            <div class="px-6" v-auto-animate>
                <div class="mt-2">
                    <div class="mt-4 gap-1.5 grid w-full items-center">
                        <Label for="name">{{
                            $t("workflows.new_workflow_modal.nameLabel")
                        }}</Label>
                        <Input
                            v-model="workflowName"
                            id="name"
                            :placeholder="
                                $t(
                                    'workflows.new_workflow_modal.name_placeholder',
                                )
                            "
                        />
                    </div>

                    <div class="mt-4 gap-1.5 grid w-full items-center">
                        <Label for="name">{{ $t("workflows.trigger") }}</Label>

                        <Select v-model="trigger">
                            <SelectTrigger class="w-full">
                                <SelectValue
                                    :placeholder="
                                        $t(
                                            'workflows.new_workflow_modal.trigger_placeholder',
                                        )
                                    "
                                />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <template
                                        v-for="(triggerType, i) in triggerTypes"
                                        :key="i"
                                    >
                                        <SelectItem
                                            :value="triggerType.value"
                                            :disabled="
                                                !hasSubscription &&
                                                triggerType.value ===
                                                    'reservation_will_happen_in'
                                            "
                                        >
                                            {{ triggerType.label }}
                                        </SelectItem>
                                    </template>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                    <div
                        v-if="trigger === 'reservation_will_happen_in'"
                        class="mt-4 gap-1 grid w-full"
                    >
                        <Label>{{
                            $t("workflows.new_workflow_modal.time_before")
                        }}</Label>
                        <InputWithSelect
                            :options="['minute', 'hour', 'day']"
                            v-model="timeBefore"
                        />
                    </div>

                    <div class="mt-4 gap-1.5 grid w-full items-center">
                        <Label for="name">{{
                            $t("workflows.new_workflow_modal.action")
                        }}</Label>

                        <Select v-model="action">
                            <SelectTrigger class="w-full">
                                <SelectValue
                                    :placeholder="
                                        $t(
                                            'workflows.new_workflow_modal.action_placeholder',
                                        )
                                    "
                                />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem
                                        v-for="(
                                            actionType, i
                                        ) in activeActionTypes"
                                        :key="i"
                                        :value="actionType.value"
                                    >
                                        {{ actionType.label }}
                                    </SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    <div
                        v-if="action === 'add_contact_to_ecomail'"
                        class="mt-4"
                    >
                        <label
                            for="workflow-name"
                            class="text-sm font-medium text-iron-700 block"
                            >{{
                                $t(
                                    "workflows.new_workflow_modal.contact_list_id",
                                )
                            }}</label
                        >
                        <div class="mt-1 w-full">
                            <input
                                v-model="contactListId"
                                type="number"
                                data-cy="new-workflow-contact-list-id"
                                name="workflow-contact-list-id"
                                class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                                :placeholder="
                                    $t(
                                        'workflows.new_workflow_modal.contact_list_id_placeholder',
                                    )
                                "
                            />
                        </div>
                    </div>

                    <div v-if="action === 'add_task_to_freelo'" class="mt-4">
                        <label
                            for="workflow-name"
                            class="text-sm font-medium text-iron-700 block"
                            >{{
                                $t("workflows.new_workflow_modal.project_id")
                            }}</label
                        >
                        <div class="mt-1 w-full">
                            <select
                                v-model="projectId"
                                data-cy="new-workflow-project-id"
                                name="project-id"
                                class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                            >
                                <option disabled :value="null">
                                    {{ projectSelectTextation }}
                                </option>
                                <option
                                    v-for="freeloProject in freeloProjects"
                                    :value="freeloProject.id"
                                    :key="freeloProject.id"
                                >
                                    {{ freeloProject.name }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div v-if="action === 'add_task_to_freelo'" class="mt-4">
                        <label
                            for="workflow-name"
                            class="text-sm font-medium text-iron-700 block"
                            >{{
                                $t("workflows.new_workflow_modal.tasklist_id")
                            }}</label
                        >
                        <div class="mt-1 w-full">
                            <select
                                v-model="tasklistId"
                                data-cy="new-workflow-tasklist-id"
                                name="tasklist-id"
                                class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                            >
                                <option disabled :value="null">
                                    {{ tasklistSelectTextation }}
                                </option>
                                <option
                                    v-for="freeloTasklist in freeloTasklists"
                                    :value="freeloTasklist.id"
                                    :key="freeloTasklist.id"
                                >
                                    {{ freeloTasklist.name }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div v-if="action === 'add_task_to_freelo'" class="mt-4">
                        <label
                            for="workflow-name"
                            class="text-sm font-medium text-iron-700 block"
                            >{{
                                $t("workflows.new_workflow_modal.worker_id") +
                                " (" +
                                $t("optional") +
                                ")"
                            }}</label
                        >
                        <div class="mt-1 w-full">
                            <select
                                v-model="workerId"
                                data-cy="new-workflow-worker-id"
                                name="worker-id"
                                class="rounded-md border-iron-300 shadow-sm focus:border-z-orange-500 focus:ring-z-orange-500 sm:text-sm block w-full"
                            >
                                <option disabled :value="null">
                                    {{ workerSelectTextation }}
                                </option>
                                <option
                                    v-for="freeloWorker in freeloWorkers"
                                    :value="freeloWorker.id"
                                    :key="freeloWorker.id"
                                >
                                    {{ freeloWorker.fullname }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <template v-if="action === 'sync_to_close'">
                        <p
                            v-if="closeLoadFailed"
                            class="mt-4 text-sm text-red-600"
                        >
                            {{ $t("workflows.close.load_failed") }}
                        </p>

                        <div class="mt-4 gap-1.5 grid w-full items-center">
                            <Label for="close-source-field">{{
                                $t("workflows.close.source_field") +
                                " (" +
                                $t("optional") +
                                ")"
                            }}</Label>
                            <Select
                                v-model="closeSourceFieldId"
                                :disabled="loadingClose"
                            >
                                <SelectTrigger
                                    id="close-source-field"
                                    class="w-full"
                                >
                                    <SelectValue
                                        :placeholder="
                                            loadingClose
                                                ? $t('workflows.close.loading')
                                                : $t('workflows.close.none')
                                        "
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem :value="CLOSE_NONE">
                                            {{ $t("workflows.close.none") }}
                                        </SelectItem>
                                        <SelectItem
                                            v-for="field in closeSourceFields"
                                            :key="field.id"
                                            :value="field.id"
                                        >
                                            {{ field.name }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <p class="text-xs text-iron-500">
                                {{ $t("workflows.close.source_field_help") }}
                            </p>
                        </div>

                        <div
                            v-if="selectedCloseSourceField"
                            class="mt-4 gap-1.5 grid w-full items-center"
                        >
                            <Label for="close-source-value">{{
                                $t("workflows.close.source_value")
                            }}</Label>
                            <Select
                                v-if="selectedCloseSourceField.choices.length"
                                v-model="closeSourceValue"
                            >
                                <SelectTrigger
                                    id="close-source-value"
                                    class="w-full"
                                >
                                    <SelectValue
                                        :placeholder="
                                            $t('workflows.select_placeholder')
                                        "
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem
                                            v-for="choice in selectedCloseSourceField.choices"
                                            :key="choice"
                                            :value="choice"
                                        >
                                            {{ choice }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <Input
                                v-else
                                v-model="closeSourceValue"
                                id="close-source-value"
                                :placeholder="
                                    $t(
                                        'workflows.close.source_value_placeholder',
                                    )
                                "
                            />
                        </div>

                        <div class="mt-4 gap-1.5 grid w-full items-center">
                            <Label for="close-status">{{
                                $t("workflows.close.status") +
                                " (" +
                                $t("optional") +
                                ")"
                            }}</Label>
                            <Select
                                v-model="closeStatusId"
                                :disabled="loadingClose"
                            >
                                <SelectTrigger id="close-status" class="w-full">
                                    <SelectValue
                                        :placeholder="
                                            $t('workflows.close.status_default')
                                        "
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem :value="CLOSE_NONE">
                                            {{
                                                $t(
                                                    "workflows.close.status_default",
                                                )
                                            }}
                                        </SelectItem>
                                        <SelectItem
                                            v-for="status in closeStatuses"
                                            :key="status.id"
                                            :value="status.id"
                                        >
                                            {{ status.label }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </div>
                    </template>

                    <div
                        v-if="
                            action !== 'send_webhook' &&
                            action !== 'sync_to_close'
                        "
                        class="mt-4 gap-1.5 grid w-full items-center"
                    >
                        <Label for="name">{{
                            $t("workflows.new_workflow_modal.target")
                        }}</Label>

                        <Select v-model="target">
                            <SelectTrigger class="w-full">
                                <SelectValue
                                    :placeholder="
                                        $t(
                                            'workflows.new_workflow_modal.target_placeholder',
                                        )
                                    "
                                />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem
                                        v-for="(
                                            targetType, i
                                        ) in activeTargetTypes"
                                        :key="i"
                                        :value="targetType.value"
                                    >
                                        {{ targetType.label }}
                                    </SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div
                    v-if="action === 'send_webhook'"
                    class="mt-4 gap-1.5 grid w-full items-center"
                >
                    <Label for="webhookIntegration">Webhook</Label>
                    <Select v-model="integrationId">
                        <SelectTrigger class="w-full">
                            <SelectValue
                                :placeholder="
                                    $t('workflows.select_placeholder')
                                "
                            />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectItem
                                    v-for="i in webhookIntegrations"
                                    :key="i.id"
                                    :value="String(i.id)"
                                >
                                    {{ i.name }} — {{ i.data?.url }}
                                </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div
                    v-if="action === 'send_email' && target === 'host'"
                    class="mt-8 gap-1.5 grid w-full items-center"
                >
                    <Label for="recipient">{{
                        $t("workflows.new_workflow_modal.recipients") +
                        " (" +
                        $t("optional") +
                        ")"
                    }}</Label>
                    <Input
                        :disabled="!hasSubscription"
                        v-model="recipients"
                        id="recipient"
                    />
                </div>

                <div
                    v-if="action === 'send_email' && target === 'host'"
                    class="mt-4 gap-1.5 grid w-full items-center"
                >
                    <Label for="cc">{{
                        $t("workflows.new_workflow_modal.cc") +
                        " (" +
                        $t("optional") +
                        ")"
                    }}</Label>
                    <Input :disabled="!hasSubscription" v-model="cc" id="cc" />
                </div>

                <div
                    v-if="action === 'send_email' && target === 'host'"
                    class="mt-4 gap-1.5 grid w-full items-center"
                >
                    <Label for="bcc">{{
                        $t("workflows.new_workflow_modal.bcc") +
                        " (" +
                        $t("optional") +
                        ")"
                    }}</Label>
                    <Input
                        :disabled="!hasSubscription"
                        v-model="bcc"
                        id="bcc"
                    />
                </div>
            </div>

            <DialogFooter>
                <DialogClose>
                    <Button variant="outline" type="button">
                        {{ $t("cancel") }}
                    </Button>
                </DialogClose>

                <Button class="ml-4" type="button" @click="createWorkflow">{{
                    $t("create")
                }}</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog-v2";
import route from "ziggy-js";
import axios from "axios";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import { InputWithSelect } from "@/components/ui/input-with-select";

import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useHasSubscription } from "@/composables/useHasSubscription";
import { useAnalytics } from "@/composables/useAnalytics";

import type { EventType } from "../../EventTypes/types/EventType";

const { hasSubscription } = useHasSubscription();
const analytics = useAnalytics();

const { t } = useI18n();

interface Props {
    integrations: any[];
    eventType?: EventType;
    defaultOpened?: boolean;
    preselectedTrigger?:
        | "reservation_made"
        | "reservation_canceled"
        | "reservation_rescheduled"
        | "reservation_will_happen_in";
    preselectedAction?:
        | "send_email"
        | "add_contact_to_ecomail"
        | "sync_to_close"
        | "send_to_slack"
        | "add_lead_to_raynet"
        | "add_task_to_freelo"
        | "add_person_to_pipedrive"
        | "add_contact_to_hubspot"
        | "send_webhook";
}

const props = defineProps<Props>();

const emit = defineEmits(["onClosed"]);

const opened = ref(props.defaultOpened ?? false);
const workflowName = ref("");

const trigger = ref<string>(props.preselectedTrigger ?? "reservation_made");
const action = ref<string>(props.preselectedAction ?? "send_email");
const target = ref<"host" | "attendees">("attendees");

const timeBefore = ref(3600);

const recipients = ref<string>();
const cc = ref<string>();
const bcc = ref<string>();

// Ecomail
const contactListId = ref<number>();

// Freelo
const projectId = ref<number>();
const tasklistId = ref<number>();
const workerId = ref<number>();
const integrationId = ref<string>("");

const freeloProjects = ref<any[]>([]);
const loadingFreeloProjects = ref(true);

const freeloWorkers = ref<any[]>([]);
const loadingFreeloWorkers = ref(true);

const freeloTasklists = ref<any[]>([]);
const loadingFreeloTasklists = ref(true);

// Close
const CLOSE_NONE = "__none";

interface CloseCustomField {
    id: string;
    name: string;
    type: string;
    choices: string[];
}

interface CloseStatus {
    id: string;
    label: string;
}

const closeCustomFields = ref<CloseCustomField[]>([]);
const closeStatuses = ref<CloseStatus[]>([]);
const loadingClose = ref(false);
const closeLoadFailed = ref(false);
const closeSourceFieldId = ref<string>(CLOSE_NONE);
const closeSourceValue = ref<string>("");
const closeStatusId = ref<string>(CLOSE_NONE);

const closeSourceFields = computed(() =>
    closeCustomFields.value.filter((field) =>
        ["text", "choices"].includes(field.type),
    ),
);

const selectedCloseSourceField = computed(
    () =>
        closeSourceFields.value.find(
            (field) => field.id === closeSourceFieldId.value,
        ) ?? null,
);

watch(selectedCloseSourceField, (field) => {
    if (field === null) {
        closeSourceValue.value = "";

        return;
    }

    closeSourceValue.value = field.choices.length
        ? (field.choices.find((choice) =>
              choice.toLowerCase().includes("demo"),
          ) ?? "")
        : "Demo call request";
});

const webhookIntegrations = computed(() => {
    return (props.integrations || []).filter((i: any) => i.type === "WEBHOOK");
});

const selectedWebhookIntegration = computed(() => {
    if (!integrationId.value) return null;
    const idNumber = Number(integrationId.value);
    return (
        webhookIntegrations.value.find((i: any) => i.id === idNumber) || null
    );
});

const triggerTypes = [
    {
        label: t("workflows.triggers.reservation_made"),
        value: "reservation_made",
    },
    {
        label: t("workflows.triggers.reservation_canceled"),
        value: "reservation_canceled",
    },
    {
        label: t("workflows.triggers.reservation_rescheduled"),
        value: "reservation_rescheduled",
    },
    // {
    //     label: t("workflows.triggers.reservation_prepared"),
    //     value: "reservation_prepared",
    // },
    {
        label: t("workflows.triggers.reservation_will_happen_in"),
        value: "reservation_will_happen_in",
    },
];

const actionTypes = [
    {
        label: t("workflows.actions.send_email"),
        value: "send_email",
    },
];

resolveAvailableActionTypes(props.integrations);

function resolveAvailableActionTypes(integrations: any) {
    const integrationTypes = integrations.map(
        (integration: any) => integration.type,
    );

    if (integrationTypes.includes("ECOMAIL")) {
        actionTypes.push({
            label: t("workflows.actions.add_contact_to_ecomail"),
            value: "add_contact_to_ecomail",
        });
    }

    if (integrationTypes.includes("CLOSE")) {
        actionTypes.push({
            label: t("workflows.actions.sync_to_close"),
            value: "sync_to_close",
        });
    }

    if (integrationTypes.includes("SLACK")) {
        actionTypes.push({
            label: t("workflows.actions.send_to_slack"),
            value: "send_to_slack",
        });
    }

    if (integrationTypes.includes("RAYNET")) {
        actionTypes.push({
            label: t("workflows.actions.add_lead_to_raynet"),
            value: "add_lead_to_raynet",
        });
    }

    if (integrationTypes.includes("FREELO")) {
        actionTypes.push({
            label: t("workflows.actions.add_task_to_freelo"),
            value: "add_task_to_freelo",
        });
    }

    if (integrationTypes.includes("PIPEDRIVE")) {
        actionTypes.push({
            label: t("workflows.actions.add_person_to_pipedrive"),
            value: "add_person_to_pipedrive",
        });
    }

    if (integrationTypes.includes("HUBSPOT")) {
        actionTypes.push({
            label: t("workflows.actions.add_contact_to_hubspot"),
            value: "add_contact_to_hubspot",
        });
    }

    if (integrationTypes.includes("WEBHOOK")) {
        actionTypes.push({
            label: t("workflows.actions.send_webhook"),
            value: "send_webhook",
        });
    }
}

const freeloIntegration = computed(() => {
    if (!props.integrations) {
        return null;
    }

    return props.integrations.find(
        (integration: any) => integration.type === "FREELO",
    );
});

const closeIntegration = computed(() => {
    return (props.integrations || []).find(
        (integration: any) => integration.type === "CLOSE",
    );
});

const targetTypes = [
    {
        label: t("workflows.targets.attendees"),
        value: "attendees",
    },
    {
        label: t("workflows.targets.host"),
        value: "host",
    },
];

const activeActionTypes = computed(() => {
    if (
        trigger.value !== "reservation_made" &&
        trigger.value !== "reservation_prepared"
    ) {
        const filtered = actionTypes.filter(
            (actionType) =>
                actionType.value !== "add_contact_to_ecomail" &&
                actionType.value !== "sync_to_close" &&
                actionType.value !== "add_lead_to_raynet" &&
                actionType.value !== "add_task_to_freelo" &&
                actionType.value !== "add_person_to_pipedrive" &&
                actionType.value !== "add_contact_to_hubspot",
        );
        return filtered.map((item) =>
            item.value === "send_webhook"
                ? {
                      ...item,
                      label: selectedWebhookIntegration.value?.name
                          ? `Send webhook — ${selectedWebhookIntegration.value.name}`
                          : item.label,
                  }
                : item,
        );
    }

    return actionTypes.map((item) =>
        item.value === "send_webhook"
            ? {
                  ...item,
                  label: selectedWebhookIntegration.value?.name
                      ? t("workflows.actions.send_webhook_with_name", {
                            name: selectedWebhookIntegration.value.name,
                        })
                      : item.label,
              }
            : item,
    );
});

const activeTargetTypes = computed(() => {
    if (
        action.value === "add_contact_to_ecomail" ||
        action.value === "sync_to_close" ||
        action.value === "send_to_slack" ||
        action.value === "add_lead_to_raynet" ||
        action.value === "add_task_to_freelo" ||
        action.value === "add_person_to_pipedrive" ||
        action.value === "add_contact_to_hubspot"
    ) {
        return targetTypes.filter(
            (targetType) => targetType.value !== "attendees",
        );
    }

    return targetTypes;
});

watch(action, (newValue) => {
    if (newValue === "add_task_to_freelo") {
        getFreeloProjects();
    }
    if (newValue === "sync_to_close") {
        getCloseOptions();
    }
    if (newValue === "send_webhook" || newValue === "sync_to_close") {
        // Target does not apply to webhook or Close; set default to satisfy backend validation
        target.value = "host";
    }
});

watch(projectId, (newValue) => {
    if (newValue) {
        getFreeloTasklists();
        tasklistId.value = undefined;
        workerId.value = undefined;
    }
});

watch(tasklistId, (newValue) => {
    if (newValue) {
        getFreeloWorkers();
        workerId.value = undefined;
    }
});

watch(opened, (newValue) => {
    if (newValue === false) {
        emit("onClosed");
    }
});

const projectSelectTextation = computed(() => {
    return loadingFreeloProjects.value
        ? t("workflows.freelo.projects_loading")
        : t("workflows.freelo.select_project");
});

const tasklistSelectTextation = computed(() => {
    if (projectId.value === null) {
        return t("workflows.freelo.select_project_first");
    }

    return loadingFreeloTasklists.value
        ? t("workflows.freelo.tasklists_loading")
        : t("workflows.freelo.select_tasklist");
});

const workerSelectTextation = computed(() => {
    if (tasklistId.value === null) {
        return t("workflows.freelo.select_tasklist_first");
    }

    return loadingFreeloWorkers.value
        ? t("workflows.freelo.workers_loading")
        : t("workflows.freelo.select_worker");
});

function createWorkflow() {
    router.post(
        route("app.workflows.store"),
        {
            name: workflowName.value,
            trigger: trigger.value,
            action: action.value,
            target: target.value,
            timeBefore:
                trigger.value === "reservation_will_happen_in"
                    ? timeBefore.value
                    : null,
            contactListId: contactListId.value,
            projectId: projectId.value,
            tasklistId: tasklistId.value,
            workerId: workerId.value,
            integrationId:
                action.value === "send_webhook"
                    ? Number(integrationId.value)
                    : undefined,
            ...(action.value === "sync_to_close"
                ? {
                      closeLeadSourceFieldId:
                          closeSourceFieldId.value !== CLOSE_NONE
                              ? closeSourceFieldId.value
                              : null,
                      closeLeadSourceValue:
                          closeSourceFieldId.value !== CLOSE_NONE
                              ? closeSourceValue.value
                              : null,
                      closeLeadStatusId:
                          closeStatusId.value !== CLOSE_NONE
                              ? closeStatusId.value
                              : null,
                  }
                : {}),
            recipients: recipients.value,
            cc: cc.value,
            bcc: bcc.value,
            // for preselected workflow
            eventTypeId: props.eventType ? props.eventType.id : undefined,
        },
        {
            onSuccess: () => {
                analytics.trackEvent("workflow_created", {
                    trigger: trigger.value,
                    action: action.value,
                    target: target.value,
                });
            },
            onFinish: () => {
                trigger.value = "reservation_made";
                action.value = "send_email";
                target.value = "attendees";
                timeBefore.value = 3600;
                closeModal();
            },
        },
    );
}

const closeModal = () => {
    opened.value = false;
};

if (action.value === "sync_to_close") {
    target.value = "host";
    getCloseOptions();
}

async function getCloseOptions() {
    if (!closeIntegration.value) {
        return;
    }

    loadingClose.value = true;
    closeLoadFailed.value = false;

    try {
        const [fieldsResponse, statusesResponse] = await Promise.all([
            axios.get(
                route("app.close-integrations.lead-custom-fields", {
                    integration: closeIntegration.value.id,
                }),
            ),
            axios.get(
                route("app.close-integrations.lead-statuses", {
                    integration: closeIntegration.value.id,
                }),
            ),
        ]);

        closeCustomFields.value = fieldsResponse.data.data;
        closeStatuses.value = statusesResponse.data.data;
    } catch (error) {
        closeLoadFailed.value = true;
    } finally {
        loadingClose.value = false;
    }
}

async function getFreeloProjects() {
    if (freeloIntegration.value === null) {
        return;
    }

    try {
        const response = await axios.get(
            route("app.freelo-integrations.projects", {
                integration: freeloIntegration.value?.id,
            }),
        );
        if (response.data.success) {
            freeloProjects.value = response.data.data;
            loadingFreeloProjects.value = false;
        }
    } catch (error) {}
}

async function getFreeloTasklists() {
    if (freeloIntegration.value === null) {
        return;
    }

    try {
        const response = await axios.get(
            route("app.freelo-integrations.tasklists", {
                integration: freeloIntegration.value.id,
                project: projectId.value,
            }),
        );
        if (response.data.success) {
            freeloTasklists.value = response.data.data;
            loadingFreeloTasklists.value = false;
        }
    } catch (error) {}
}

async function getFreeloWorkers() {
    if (freeloIntegration.value === null) {
        return;
    }

    try {
        const response = await axios.get(
            route("app.freelo-integrations.users", {
                integration: freeloIntegration.value.id,
                project: projectId.value,
                tasklist: tasklistId.value,
            }),
        );
        if (response.data.success) {
            freeloWorkers.value = response.data.data;
            loadingFreeloWorkers.value = false;
        }
    } catch (error) {
        console.log(error);
    }
}
</script>
